import type { RegisterOptions } from "react-hook-form";
import type { FindPlaceCandidate } from "../apis/google";
import * as Constants from "./constants";
import {
  format,
  isTomorrow,
  isToday,
  isValid,
  parseISO,
  startOfDay,
} from "date-fns";

type TranslationFn = (key: string, options?: Record<string, unknown>) => string;

function normalizePhone(value: string) {
  return value.trim().replace(/[^\d+]/g, "");
}

function isValidPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, "");
  const hit = Constants.phoneMeta.find((m) =>
    digits.startsWith(m.dial.slice(1)),
  );
  if (!hit) return Boolean(hit);
  const nationalDigits = digits.slice(hit.dial.length - 1);
  return nationalDigits.length === hit.nationalMaxDigits;
}

function hasText(value: unknown) {
  return typeof value === "string" && value.trim().length > 0;
}

function splitName(value: string | null | undefined) {
  const [firstName, lastName] = value?.split(" ") ?? [];
  return { firstName: firstName ?? "", lastName: lastName ?? "" };
}

function parseYmdToDay(iso: string | null | undefined): Date | undefined {
  if (!iso || !isValid(parseISO(iso))) return undefined;
  return startOfDay(parseISO(iso));
}

function formatSmartDate(iso: string) {
  const d = parseISO(iso);
  if (!isValid(d)) return iso;
  const includeTime = iso.includes("T");
  const time = includeTime ? ` (${format(d, "HH:mm")})` : "";
  if (isToday(d)) return `Today${time}`;
  if (isTomorrow(d)) return `Tomorrow${time}`;
  return format(d, includeTime ? `d MMMM (HH:mm)` : "EEE d").toUpperCase();
}

function formatYmd(iso: string, pattern: string) {
  const d = parseISO(iso);
  if (!isValid(d)) return iso;
  return format(d, pattern);
}

export function getCountryCodeFromPlace(place?: FindPlaceCandidate | null) {
  return (
    place?.addressComponents?.find((c) => c.types?.includes("country"))
      ?.shortText ?? ""
  );
}

function requiredText(message: string) {
  return (value: string) => hasText(value) || message;
}

function formatEur(amount: number) {
  return `€${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}`;
}

function bookingNameRules(t: TranslationFn): RegisterOptions {
  return {
    required: true,
    minLength: { value: 2, message: t("booking.validation.nameMinLength") },
    maxLength: { value: 50, message: t("booking.validation.nameMaxLength") },
  };
}

function bookingEmailRules(t: TranslationFn): RegisterOptions {
  return {
    required: true,
    validate: {
      requiredTrimmed: requiredText(t("booking.validation.emailRequired")),
      validFormat: (v: string) =>
        Tools.isValidEmail(v.trim()) || t("booking.validation.invalidEmail"),
    },
  };
}

function bookingPolicyRules(t: TranslationFn): RegisterOptions {
  return {
    required: true,
    minLength: { value: 3, message: t("booking.validation.policyMinLength") },
    maxLength: { value: 50, message: t("booking.validation.policyMaxLength") },
    validate: {
      requiredTrimmed: requiredText(t("booking.validation.policyRequired")),
    },
  };
}

function bookingInsuranceRules(t: TranslationFn): RegisterOptions {
  return {
    required: true,
    minLength: {
      value: 2,
      message: t("booking.validation.insuranceMinLength"),
    },
    maxLength: {
      value: 100,
      message: t("booking.validation.insuranceMaxLength"),
    },
    validate: {
      requiredTrimmed: requiredText(t("booking.validation.insuranceRequired")),
      validFormat: (v: string) =>
        /^[A-Za-zÀ-ÖØ-öø-ÿ0-9&.,' -]+$/.test(v.trim()) ||
        t("booking.validation.invalidInsurance"),
    },
  };
}

function bookingPhoneRules(t: TranslationFn): RegisterOptions {
  return {
    required: true,
    validate: {
      validFormat: (v: string) =>
        isValidPhoneNumber(v) || t("booking.validation.invalidMobile"),
    },
  };
}

function printElement(element?: HTMLElement | null) {
  if (!element) return;
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");

  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;

  if (!doc || !win) {
    iframe.remove();
    return;
  }

  const styles = Array.from(
    document.querySelectorAll('link[rel="stylesheet"], style'),
  )
    .map((node) => {
      if (node.tagName === "LINK") {
        const link = node as HTMLLinkElement;
        return `<link rel="stylesheet" href="${link.href}" />`;
      }
      return node.outerHTML;
    })
    .join("");

  doc.open();
  doc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title> </title>
        ${styles}
        <style>
          @page {
            size: auto;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
          }
          body {
            margin: 0;
            padding: 5mm;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        </style>
      </head>
      <body></body>
    </html>
  `);

  doc.close();

  // Inject content AFTER document is ready
  const container = doc.createElement("div");
  container.innerHTML = element.outerHTML;
  doc.body.appendChild(container);

  // Extra attempt to avoid title fallback
  doc.title = "";
  try {
    win.document.title = "";
  } catch {}

  const cleanup = () => {
    iframe.remove();
  };

  win.onafterprint = cleanup;

  setTimeout(() => {
    win.focus();
    win.print();

    // fallback cleanup
    setTimeout(cleanup, 1500);
  }, 100);
}

function getByPath<T = any>(obj: any, path: string): T | undefined {
  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

const formatKm = (distanceKm: number) => {
  if (!Number.isFinite(distanceKm)) return "0 m";
  if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} m`;
  return `${Math.round(distanceKm)} km`;
};

const Tools = {
  isValidEmail: (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  },
  formatKm,
  getByPath,
  formatEur,
  normalizePhone,
  isValidPhoneNumber,
  hasText,
  splitName,
  parseYmdToDay,
  formatSmartDate,
  formatYmd,
  getCountryCodeFromPlace,
  requiredText,
  bookingNameRules,
  bookingEmailRules,
  bookingPolicyRules,
  bookingInsuranceRules,
  bookingPhoneRules,
  printElement,
};

export default Tools;
